import { authFetch } from 'auth/utils';
import { Tag, TagsFilters } from 'tags/models/tags';
import { prepareTagsFilterParams } from 'tags/utils/searchParams';

type CreateTagApiServiceProps = {
  baseApiPath: string;
};

export const createTagApiService = ({ baseApiPath }: CreateTagApiServiceProps) => {
  const fetchTags = async (filters?: TagsFilters, signal?: AbortSignal) => {
    const searchParams = prepareTagsFilterParams(filters);
    const tagsResponse = await authFetch(`${baseApiPath}/tags?${searchParams}`, {
      signal,
    });

    const tags: Tag[] = await tagsResponse.json();
    return tags;
  };

  const fetchTag = async (id: string, signal?: AbortSignal) => {
    const tagsResponse = await authFetch(`${baseApiPath}/tags/${id}`, {
      signal,
    });

    const tag: Tag = await tagsResponse.json();

    return tag;
  };

  const createTag = async (name: string, signal?: AbortSignal) => {
    const urlQuery = new URLSearchParams();
    urlQuery.append('name', name);

    const tagsResponse = await authFetch(`${baseApiPath}/tags?${urlQuery.toString()}`, {
      signal,
      method: 'POST',
    });

    const tag: Tag = await tagsResponse.json();

    return tag;
  };

  return {
    fetchTags,
    fetchTag,
    createTag,
  };
};

type CreateAsignTagApiServiceProps = {
  baseApiPath: string;
  cardIdName: string;
  cardArrayIdsName: string;
};

export const createAsignTagApi = <TFetchFilter, TAssignedDTO extends any[]>({
  baseApiPath,
  cardIdName,
  cardArrayIdsName,
}: CreateAsignTagApiServiceProps) => {
  const fetchAssignedTagsToCards = async (filters: TFetchFilter, signal?: AbortSignal) => {
    const tagsResponse = await authFetch(`${baseApiPath}/tags/assigned`, {
      signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filters),
    });

    const assignedTags: TAssignedDTO = await tagsResponse.json();

    return assignedTags;
  };

  const fetchAssignedTagsToCard = async (cardId: string, _signal?: AbortSignal) => {
    const assignedTags: TAssignedDTO = await fetchAssignedTagsToCards({
      [cardArrayIdsName]: [cardId],
    } as unknown as TFetchFilter);

    return assignedTags.length ? assignedTags[0].tags : [];
  };

  const assignTagsToCard = async (cardId: string, tagIds: string[], signal?: AbortSignal) => {
    await authFetch(`${baseApiPath}/tags/assign`, {
      signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        [cardIdName]: cardId,
        tagIds,
      }),
    });
  };

  return { fetchAssignedTagsToCard, fetchAssignedTagsToCards, assignTagsToCard };
};
